import React, { useState } from 'react';
import { api, API_BASE_URL } from '../../api/client';

const API_URL = API_BASE_URL;

function getVenueQuery(): string {
  const venueId = api.getSelectedVenueId();
  return venueId ? `?venueId=${venueId}` : '';
}

// Default pricing in cents
const DEFAULT_INDOOR_RATE_CENTS = 3000;  // $30/hour
const DEFAULT_OUTDOOR_RATE_CENTS = 2000; // $20/hour

interface Court {
  id: string;
  name: string;
  type: string;
  sport: string;
  isActive: boolean;
  hourlyRateCents: number;
}

interface ScheduleSlot {
  time: string;
  timeMinutes: number;
  available: number;
  hasWaitlist: boolean;
  status: string;
}

interface CourtAvailability {
  id: string;
  name: string;
  available: boolean;
}

interface AvailabilitySlot {
  time: string;
  courts: CourtAvailability[];
}

interface DayAvailability {
  date: string;
  slots: AvailabilitySlot[];
}

interface NormalizedData {
  facilityId: string;
  venueName: string;
  totalCourts: number;
  courts: Court[];
  schedule: ScheduleSlot[];
  operatingHours: {
    open: string | null;
    close: string | null;
  };
  rawAvailability?: DayAvailability[];
}

interface ScrapeResult {
  success: boolean;
  mode: 'widget' | 'expanded' | 'bookings';
  data: NormalizedData;
  scrapeId?: string;
}

type Step = 'input' | 'loading' | 'validation' | 'success' | 'error';

interface Props {
  onClose: () => void;
}

export default function CourtReserveIntegration({ onClose }: Props) {
  const token = api.getToken();
  const [step, setStep] = useState<Step>('input');
  const [facilityId, setFacilityId] = useState('');
  const [error, setError] = useState('');
  const [scrapeResult, setScrapeResult] = useState<ScrapeResult | null>(null);
  const [editableCourts, setEditableCourts] = useState<Court[]>([]);
  const [approving, setApproving] = useState(false);

  const handleScrapeWithValues = async (fId: string) => {
    setStep('loading');

    try {
      const response = await fetch(`${API_URL}/api/integrations/courtreserve/preview${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ facilityId: fId })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch schedule data');
      }

      setScrapeResult(result);
      // Add default pricing based on court type
      const courtsWithPricing = (result.data.courts || []).map((court: Court) => ({
        ...court,
        hourlyRateCents: court.hourlyRateCents ||
          (court.type === 'outdoor' ? DEFAULT_OUTDOOR_RATE_CENTS : DEFAULT_INDOOR_RATE_CENTS)
      }));
      setEditableCourts(courtsWithPricing);
      setStep('validation');
    } catch (err: any) {
      setError(err.message || 'Failed to connect to CourtReserve');
      setStep('error');
    }
  };

  const handleScrape = async () => {
    if (!facilityId.trim()) {
      setError('Please enter your CourtReserve Facility ID');
      return;
    }

    setError('');
    handleScrapeWithValues(facilityId.trim());
  };

  const handleUpdateCourt = (index: number, field: keyof Court, value: string | boolean | number) => {
    const updated = [...editableCourts];
    updated[index] = { ...updated[index], [field]: value };
    // Auto-update pricing when court type changes
    if (field === 'type') {
      updated[index].hourlyRateCents = value === 'outdoor' ? DEFAULT_OUTDOOR_RATE_CENTS : DEFAULT_INDOOR_RATE_CENTS;
    }
    setEditableCourts(updated);
  };

  const handleRemoveCourt = (index: number) => {
    setEditableCourts(editableCourts.filter((_, i) => i !== index));
  };

  const handleAddCourt = () => {
    const newCourt: Court = {
      id: `court-${editableCourts.length + 1}`,
      name: `Court ${editableCourts.length + 1}`,
      type: 'indoor',
      sport: 'pickleball',
      isActive: true,
      hourlyRateCents: DEFAULT_INDOOR_RATE_CENTS
    };
    setEditableCourts([...editableCourts, newCourt]);
  };

  const handleApprove = async () => {
    setApproving(true);
    setError('');

    try {
      // Use the unified import endpoint that clears existing data and creates courts + availability
      const importResponse = await fetch(`${API_URL}/api/integrations/import${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          integrationType: 'courtreserve',
          externalId: facilityId.trim(),
          config: {
            venueName: scrapeResult?.data.venueName,
            mode: scrapeResult?.mode
          },
          courts: editableCourts,
          schedule: scrapeResult?.data.schedule,
          operatingHours: scrapeResult?.data.operatingHours,
          rawAvailability: scrapeResult?.data.rawAvailability
        })
      });

      const result = await importResponse.json();

      if (!importResponse.ok) {
        throw new Error(result.error || 'Failed to import courts');
      }

      console.log('Import result:', result);
      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Failed to save courts');
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="integration-setup">
      {/* Header */}
      <div className="integration-setup__header">
        <button className="integration-setup__back" onClick={onClose}>
          <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Integrations
        </button>
        <div className="integration-setup__title">
          <div className="integration-setup__logo" style={{ background: '#2E7D32' }}>C</div>
          <div>
            <h1>CourtReserve Integration</h1>
            <p>Import your courts and availability schedule</p>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="integration-setup__steps">
        <div className={`step ${step === 'input' ? 'step--active' : 'step--completed'}`}>
          <span className="step__number">1</span>
          <span className="step__label">Connect</span>
        </div>
        <div className="step__line" />
        <div className={`step ${step === 'loading' ? 'step--active' : (['validation', 'success'].includes(step) ? 'step--completed' : '')}`}>
          <span className="step__number">2</span>
          <span className="step__label">Fetch Data</span>
        </div>
        <div className="step__line" />
        <div className={`step ${step === 'validation' ? 'step--active' : (step === 'success' ? 'step--completed' : '')}`}>
          <span className="step__number">3</span>
          <span className="step__label">Review & Approve</span>
        </div>
      </div>

      {/* Content */}
      <div className="integration-setup__content">
        {/* Step 1: Input */}
        {step === 'input' && (
          <div className="integration-step">
            <div className="integration-step__card">
              <h2>Enter CourtReserve Facility ID</h2>
              <p>Enter your CourtReserve Facility ID from your portal URL.</p>

              <div className="form-group">
                <label htmlFor="facilityId">Facility ID <span className="required">*</span></label>
                <input
                  type="text"
                  id="facilityId"
                  value={facilityId}
                  onChange={(e) => setFacilityId(e.target.value)}
                  placeholder="e.g., 12546"
                  className="form-input"
                />
                <p className="form-help">
                  Find this in your CourtReserve URL: <code>app.courtreserve.com/Online/Portal/Index/<strong>12546</strong></code>
                </p>
              </div>

              {error && <div className="alert alert--error">{error}</div>}

              <div className="integration-step__actions">
                <button className="btn btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleScrape}>
                  Connect & Fetch Data
                </button>
              </div>
            </div>

            <div className="integration-step__help">
              <h3>How to find your Facility ID</h3>
              <ol>
                <li>Log in to your CourtReserve account</li>
                <li>Go to your facility's portal</li>
                <li>Look at the URL in your browser</li>
                <li>The number after "Index/" is your Facility ID</li>
              </ol>
            </div>
          </div>
        )}

        {/* Step 2: Loading */}
        {step === 'loading' && (
          <div className="integration-step integration-step--loading">
            <div className="loading-animation">
              <div className="loading-spinner" />
              <h2>Fetching Your Schedule Data</h2>
              <p>We're connecting to CourtReserve and importing your court information...</p>
              <div className="loading-steps">
                <div className="loading-step loading-step--active">
                  <span className="loading-step__icon">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </span>
                  Connecting to CourtReserve...
                </div>
                <div className="loading-step">
                  <span className="loading-step__icon" />
                  Reading availability schedule...
                </div>
                <div className="loading-step">
                  <span className="loading-step__icon" />
                  Detecting courts...
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Validation */}
        {step === 'validation' && scrapeResult && (
          <div className="integration-step integration-step--validation">
            <div className="validation-layout">
              {/* Left: Courts Editor */}
              <div className="validation-section">
                <div className="validation-section__header">
                  <h2>Detected Courts</h2>
                  <span className="badge">{editableCourts.length} courts</span>
                </div>
                <p>Review and edit the courts we detected from your CourtReserve account.</p>

                <div className="courts-editor">
                  {editableCourts.map((court, index) => (
                    <div key={court.id} className="court-editor-row">
                      <input
                        type="text"
                        value={court.name}
                        onChange={(e) => handleUpdateCourt(index, 'name', e.target.value)}
                        className="form-input form-input--name"
                        placeholder="Court name"
                      />
                      <select
                        value={court.type}
                        onChange={(e) => handleUpdateCourt(index, 'type', e.target.value)}
                        className="form-select"
                      >
                        <option value="indoor">Indoor</option>
                        <option value="outdoor">Outdoor</option>
                        <option value="covered">Covered</option>
                      </select>
                      <select
                        value={court.sport}
                        onChange={(e) => handleUpdateCourt(index, 'sport', e.target.value)}
                        className="form-select"
                      >
                        <option value="pickleball">Pickleball</option>
                        <option value="tennis">Tennis</option>
                        <option value="basketball">Basketball</option>
                      </select>
                      <div className="form-input-group form-input-group--price">
                        <span className="form-input-prefix">$</span>
                        <input
                          type="number"
                          value={(court.hourlyRateCents / 100).toFixed(0)}
                          onChange={(e) => handleUpdateCourt(index, 'hourlyRateCents', Math.round(parseFloat(e.target.value || '0') * 100))}
                          className="form-input form-input--price"
                          min="0"
                          step="1"
                        />
                        <span className="form-input-suffix">/hr</span>
                      </div>
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={court.isActive}
                          onChange={(e) => handleUpdateCourt(index, 'isActive', e.target.checked)}
                        />
                        <span className="toggle-text">Active</span>
                      </label>
                      <button
                        className="btn-icon btn-icon--danger"
                        onClick={() => handleRemoveCourt(index)}
                        title="Remove court"
                      >
                        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}

                  <button className="btn btn-secondary btn--add-court" onClick={handleAddCourt}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add Court
                  </button>
                </div>
              </div>

              {/* Right: Schedule Preview */}
              <div className="validation-section">
                <div className="validation-section__header">
                  <h2>Today's Schedule</h2>
                  <span className="badge badge--info">Preview</span>
                </div>
                <p>Here's the availability we detected for today.</p>

                <div className="schedule-preview">
                  <div className="schedule-info">
                    <div className="schedule-info__item">
                      <span className="schedule-info__label">Operating Hours</span>
                      <span className="schedule-info__value">
                        {scrapeResult.data.operatingHours.open} - {scrapeResult.data.operatingHours.close}
                      </span>
                    </div>
                    <div className="schedule-info__item">
                      <span className="schedule-info__label">Total Courts</span>
                      <span className="schedule-info__value">{scrapeResult.data.totalCourts}</span>
                    </div>
                  </div>

                  <div className="schedule-slots">
                    {scrapeResult.data.schedule.slice(0, 10).map((slot, index) => (
                      <div key={index} className={`schedule-slot schedule-slot--${slot.status}`}>
                        <span className="schedule-slot__time">{slot.time}</span>
                        <span className="schedule-slot__availability">
                          {slot.status === 'unavailable' ? 'Unavailable' :
                           slot.status === 'full' ? (slot.hasWaitlist ? 'Waitlist' : 'Full') :
                           `${slot.available} available`}
                        </span>
                      </div>
                    ))}
                    {scrapeResult.data.schedule.length > 10 && (
                      <div className="schedule-slot schedule-slot--more">
                        +{scrapeResult.data.schedule.length - 10} more slots
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {error && <div className="alert alert--error">{error}</div>}

            <div className="integration-step__actions">
              <button className="btn btn-secondary" onClick={() => setStep('input')}>
                Back
              </button>
              <button className="btn btn-primary" onClick={handleApprove} disabled={approving}>
                {approving ? 'Creating Courts...' : `Approve & Create ${editableCourts.length} Courts`}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 'success' && (
          <div className="integration-step integration-step--success">
            <div className="success-card">
              <div className="success-icon">
                <svg viewBox="0 0 20 20" fill="currentColor" width="48" height="48">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h2>Integration Complete!</h2>
              <p>
                Your courts have been created and your CourtReserve integration is now active.
                We'll automatically sync your availability schedule.
              </p>

              <div className="success-stats">
                <div className="success-stat">
                  <span className="success-stat__value">{editableCourts.length}</span>
                  <span className="success-stat__label">Courts Created</span>
                </div>
                <div className="success-stat">
                  <span className="success-stat__value">Active</span>
                  <span className="success-stat__label">Sync Status</span>
                </div>
              </div>

              <div className="integration-step__actions">
                <button className="btn btn-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {step === 'error' && (
          <div className="integration-step integration-step--error">
            <div className="error-card">
              <div className="error-icon">
                <svg viewBox="0 0 20 20" fill="currentColor" width="48" height="48">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h2>Connection Failed</h2>
              <p>{error || 'We couldn\'t connect to CourtReserve. Please check your Facility ID and try again.'}</p>

              <div className="integration-step__actions">
                <button className="btn btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={() => setStep('input')}>
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
